import { keepPreviousData, useQuery } from "@tanstack/react-query";
import type { PagedResult } from "../../../../components/PortfolioPerformance/pager";
import { apiGet, apiGetBlob } from "./client";
import { useTicketSize, type TicketSize } from "../ticket-size";

// Keys match app/repository/watchlist.py's own DOWNLOAD_TABLES whitelist on
// the backend -- never send an arbitrary table name, only one of these.
export const APD_DOWNLOAD_TABLES = [
  { key: "sm-sic", label: "SM SIC Industry", filename: "sm_sic_industry.csv" },
  { key: "sm-asset-industry", label: "SM Summary Equip Type", filename: "sm_summary_equip_type.csv" },
  { key: "asset-sic-apd", label: "APD", filename: "apd.csv" },
] as const;

/** CORS doesn't expose Content-Disposition cross-origin, so the filename is
 * hardcoded here rather than read off the response (same fallback
 * portfolio-risk-concentration's own CSV download already relies on). */
export async function downloadApdTable(key: string, filename: string, ticket?: TicketSize): Promise<void> {
  const blob = await apiGetBlob(`/apd-monitoring/download/${key}`, { ticket });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export interface ApdTrendPoint {
  month: string;
  apd: number;
}

// The three cards above the filters: Special Mention, Substandard and Doubtful/Loss. `ni` is that sub-category's NI
// (formatted), `trend` its change against the previous file date, `share` its part of the three together.
export interface SubCategoryCard {
  subCategory: string;
  ni: string;
  share: string;
  trend: { value: string; direction: "up" | "down" | "flat" };
  /** The month the trend is measured against, named ("Sep 2026"); null when there is nothing to compare with. */
  comparedTo?: string | null;
}

// Type aliases (not interfaces) so rows satisfy SmTable's Record<string, string>.
export type SmIndustryRow = {
  sic: string;
  industry: string;
  ni: string;
  pctOfTotal: string;
  amount30Plus: string;
  delPct: string;
  apdPct: string;
};
export type SmEquipmentRow = {
  code: string;
  equipmentType: string;
  ni: string;
  pctOfTotal: string;
  amount30Plus: string;
  delPct: string;
  apdPct: string;
};

/** One sub-category of the APD grid (Pass, Watch, Special Mention, ...) with its limits in percent. */
export interface ApdBand {
  label: string;
  /** the grid's category (Pass, Watch, Special Mention and Worse); the band's colour follows it */
  category: string;
  fromPct: number;
  toPct: number;
}

export interface ApdMonitoringSummary {
  trend: ApdTrendPoint[];
  smThreshold: number;
  subCategoryCards: SubCategoryCard[];
  /** the grid's sub-categories, mildest first; empty when the grid cannot be read */
  bands?: ApdBand[];
}

export function useApdMonitoring() {
  const { ticket } = useTicketSize();
  return useQuery({
    queryKey: ["apd-monitoring", "summary", ticket ?? "all"],
    queryFn: () => apiGet<ApdMonitoringSummary>("/apd-monitoring", { ticket }),
  });
}

// Portfolio Risk Class options: every category in the APD grid, mildest first, so a new category appears
// without a code change.
export function useSmRiskClasses() {
  return useQuery({
    queryKey: ["apd-monitoring", "risk-classes"],
    queryFn: () => apiGet<string[]>("/apd-monitoring/risk-classes"),
    staleTime: 5 * 60_000,
  });
}

// Sub Category options for one category: only the sub-categories the APD grid maps to it (Pass has just
// Pass; Special Mention and Worse has Special Mention, Substandard and Doubtful/Loss), mildest first.
// Waits for a category, and is keyed by it so changing the category never shows the previous list.
export function useSmSubCategories(riskClass?: string) {
  return useQuery({
    enabled: !!riskClass,
    queryKey: ["apd-monitoring", "sub-categories", riskClass ?? null],
    queryFn: () => apiGet<string[]>("/apd-monitoring/sub-categories", { riskClass }),
    staleTime: 5 * 60_000,
  });
}

// The Portfolio Risk Class the Special Mention tables open on.
export const DEFAULT_SM_CLASS = "Special Mention and Worse";

/**
 * The Special Mention tables' DEFAULT view, as APD Monitoring opens it with nothing picked: the default class (or
 * the first one if that does not exist) and the first Sub Category mapped to it. For a page that shows the same
 * table without the dropdowns. `subFilter` follows useSmIndustryPage's meaning: undefined while the list loads,
 * null when the class has no sub-categories.
 */
export function useDefaultSmScope() {
  const classes = useSmRiskClasses();
  const options = classes.data ?? [];
  const riskClass = options.includes(DEFAULT_SM_CLASS) ? DEFAULT_SM_CLASS : options[0];
  const subs = useSmSubCategories(riskClass);
  const subCategory = (subs.data ?? [])[0];
  const subFilter = subCategory ?? (subs.isSuccess ? null : undefined);
  return { riskClass, subCategory, subFilter };
}

// Server-side sort applied to the WHOLE table before paging. "key" is SIC / Code, "label" is Industry /
// Equipment Type; the rest are the numeric columns.
export type SmSortKey = "key" | "label" | "ni" | "pctOfTotal" | "amount30Plus" | "delPct" | "apdPct";
export interface SmSort {
  key: SmSortKey;
  dir: "asc" | "desc";
}
export const DEFAULT_SM_SORT: SmSort = { key: "ni", dir: "desc" };

// Each Special Mention table loads its top 25 rows in one request (the page pages through them 10 at a time,
// client-side) and shares the Portfolio Risk Class and Sub Category filters. The sort is still the server's:
// it ranks the whole table and the 25 returned are the top of that ranking. The query waits until a class is chosen and the sub-category is settled:
// `subCategory` is undefined while its list is still loading, null when the category has none (no sub filter),
// otherwise the chosen one. keepPreviousData keeps the current rows on screen while the next page or filter loads.
export function useSmIndustryPage(
  riskClass?: string,
  sort: SmSort = DEFAULT_SM_SORT,
  subCategory?: string | null,
) {
  const { ticket } = useTicketSize();
  return useQuery({
    enabled: !!riskClass && subCategory !== undefined,
    queryKey: ["apd-monitoring", "sm-industry", riskClass ?? null, subCategory ?? null, sort.key, sort.dir, ticket ?? "all"],
    queryFn: () =>
      apiGet<PagedResult<SmIndustryRow>>("/apd-monitoring/sm-industry", {
        ticket,
        riskClass,
        subCategory: subCategory ?? undefined,
        sortBy: sort.key,
        sortDir: sort.dir,
      }),
    placeholderData: keepPreviousData,
  });
}

export function useSmEquipmentPage(
  riskClass?: string,
  sort: SmSort = DEFAULT_SM_SORT,
  subCategory?: string | null,
) {
  const { ticket } = useTicketSize();
  return useQuery({
    enabled: !!riskClass && subCategory !== undefined,
    queryKey: ["apd-monitoring", "sm-equipment", riskClass ?? null, subCategory ?? null, sort.key, sort.dir, ticket ?? "all"],
    queryFn: () =>
      apiGet<PagedResult<SmEquipmentRow>>("/apd-monitoring/sm-equipment", {
        ticket,
        riskClass,
        subCategory: subCategory ?? undefined,
        sortBy: sort.key,
        sortDir: sort.dir,
      }),
    placeholderData: keepPreviousData,
  });
}
